'use client';

/**
 * @fileoverview CharacterContextPanelProvider — UI state for the
 * "Characters in view" side panel surfaced on the writing/editing pages.
 *
 * Mirrors the chat panel pattern: per-project open/closed state, plus a
 * width that persists in localStorage as a screen-size preference.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useActiveProject } from './active-project-context';

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 240;
const MAX_WIDTH = 560;
const WIDTH_STORAGE_KEY = 'character-context-panel:width';

interface CharacterContextPanelValue {
  isOpen: boolean;
  width: number;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  minWidth: number;
  maxWidth: number;
}

const CharacterContextPanelContext = createContext<
  CharacterContextPanelValue | undefined
>(undefined);

export function CharacterContextPanelProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { activeProject } = useActiveProject();
  const projectId = activeProject?.id ?? null;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [projectId]);

  const [width, setWidthState] = useState<number>(DEFAULT_WIDTH);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(WIDTH_STORAGE_KEY);
    if (!stored) return;
    const parsed = Number(stored);
    if (Number.isFinite(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
      setWidthState(parsed);
    }
  }, []);

  const setWidth = (next: number) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, next));
    setWidthState(clamped);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped));
    }
  };

  const value = useMemo<CharacterContextPanelValue>(
    () => ({
      isOpen,
      width,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((prev) => !prev),
      setWidth,
      minWidth: MIN_WIDTH,
      maxWidth: MAX_WIDTH,
    }),
    [isOpen, width],
  );

  return (
    <CharacterContextPanelContext.Provider value={value}>
      {children}
    </CharacterContextPanelContext.Provider>
  );
}

export function useCharacterContextPanel(): CharacterContextPanelValue {
  const ctx = useContext(CharacterContextPanelContext);
  if (!ctx) {
    throw new Error(
      'useCharacterContextPanel must be used within a CharacterContextPanelProvider',
    );
  }
  return ctx;
}
